// React & core
import { useEffect, useMemo, useRef, useState } from "react";

// Animation / Routing
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router";

// Icons
import {
  X,
  Mail,
  Phone,
  Building2,
  MapPin,
  UserCircle2,
  CalendarDays,
  IdCard,
  Clock,
  Copy,
  ExternalLink,
  ChevronRight,
  Briefcase,
  Monitor,
  Search,
  StickyNote,
  FileText,
  User,
  Globe,
  Quote,
  Building,
  Calendar,
  Award,
  CheckCircle,
  Bell,
  Users,
  Map,
  Globe2,
  Layers,
  MailCheck,
  PhoneCall,
} from "lucide-react";

// Custom hooks & UI
import { useApiCall } from "../../../../../component/custom-hooks/api-call";
import InnerLoader from "../../../../../component/ui/loader/inner-loader";

const tabs = [
  { key: "summary", label: "Summary" },
  { key: "customer_information", label: "Customer Detailed Information" },
  { key: "transactions", label: "Transactions" },
  { key: "client_deals", label: "Deals" },
  { key: "client_projects", label: "Projects" },
];

function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}

function Badge({ children, tone = "default", icon: Icon }) {
  const tones = {
    default: "bg-slate-100 text-slate-700",
    green: "bg-emerald-100 text-emerald-700",
    blue: "bg-blue-100 text-blue-700",
    amber: "bg-amber-100 text-amber-800",
    red: "bg-rose-100 text-rose-700",
    grey: "bg-neutral-100 text-neutral-700",
  };
  return (
    <span
      className={classNames(
        "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium",
        tones[tone]
      )}
    >
      {Icon ? <Icon size={14} /> : null}
      {children}
    </span>
  );
}

function Stat({ title, value, icon: Icon, hint, tone = "default" }) {
  return (
    <div className="group relative rounded-2xl border border-solid border-slate-200 bg-white p-4 shadow-sm hover:shadow-md">
      <div className="flex items-start gap-3">
        <div className="min-w-0">
          <div className="text-xs text-slate-500 capitalize">
            {title.includes("_")
              ? title.split("_").map((word) => ` ${word}`)
              : title}
          </div>
          {/* truncate */}
          <div className="text-lg font-semibold tracking-tight">{value}</div>
          {hint && <div className="mt-1 text-xs text-slate-400">{hint}</div>}
        </div>
      </div>
    </div>
  );
}
function Field({ label, value, icon: Icon, copyable, link }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="grid grid-cols-12 items-center gap-2 rounded-xl border border-solid border-slate-200 bg-white p-3 transition hover:bg-slate-50">
      <div className="col-span-4 flex items-center gap-2 text-[13px] font-medium text-slate-500">
        {Icon ? <Icon className="size-4" /> : null}
        {label}
      </div>
      <div className="col-span-8 flex items-center justify-between gap-2 text-[13px] text-slate-800">
        {link ? (
          <a
            href={link}
            className="truncate underline decoration-slate-300 underline-offset-2 hover:text-blue-600 hover:decoration-blue-400"
          >
            {value}
          </a>
        ) : (
          <span className="truncate">{value}</span>
        )}
        {copyable && (
          <button
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(String(value ?? ""));
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
              } catch {}
            }}
            className="inline-flex items-center gap-1 rounded-md border border-solid border-slate-200 bg-white px-2 py-1 text-[11px] text-slate-600 hover:bg-slate-100"
          >
            <Copy className="size-3.5" /> {copied ? "Copied" : "Copy"}
          </button>
        )}
      </div>
    </div>
  );
}

export default function AccountQuickView({
  open = true,
  onClose = () => {},
  data,
}) {
  const [contact, setcontact] = useState({});
  const [activeTab, setActiveTab] = useState("summary");
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState(contact?.contactstatus);
  const containerRef = useRef(null);
  const apiCall = useApiCall();
  const [isLoading, setIsLoading] = useState(false);

  const GetData = async (id) => {
    let apiData = JSON.stringify({
      userid: id,
    });

    try {
      let call = await apiCall({
        method: "get",
        url: `accounts/get-account/${id}`,
        data: apiData,
      });
      let ContactData = call?.data;

      console.log("ContactData", ContactData);
      setIsLoading(true);
      let activity = ContactData?.recent_activity?.map((item) => {
        const dateObj = new Date(item.time);
        const formattedDate = dateObj
          .toISOString()
          .slice(0, 16)
          .replace("T", " ");
        // This gives "YYYY-MM-DD HH:MM"

        return {
          ts: formattedDate,
          text: item.data,
        };
      });

      const sdata = {
        // ---------- SUMMARY TAB ----------
        id: ContactData?.userid,
        name: ContactData?.name,
        email: ContactData?.email,
        phone: ContactData?.phone,
        source: ContactData?.source,
        page_source: ContactData?.page_source,
        created_at: ContactData?.created_at,

        // ---------- CUSTOMER INFORMATION TAB ----------
        altemail: ContactData?.altemail,
        alternatephone: ContactData?.alternatephone,
        usertype: ContactData?.usertype,
        country: ContactData?.country,
        tagline: ContactData?.tagline,
        userimg: ContactData?.userimg,
        companyname: ContactData?.companyname,
        address: ContactData?.address,
        city: ContactData?.city,
        state: ContactData?.state,
        zipcode: ContactData?.zipcode,
        verified: ContactData?.verified,
        verifykey: ContactData?.verifykey,
        badge: ContactData?.badge,
        view_tutorial: ContactData?.view_tutorial,
        active: ContactData?.active,
        theme: ContactData?.theme,
        assigned: ContactData?.assigned,
        notify: ContactData?.notify,
        teamleadid: ContactData?.teamleadid,
        revoke_user: ContactData?.revoke_user,
        is_private: ContactData?.is_private,
        is_private_assignee: ContactData?.is_private_assignee,
        signupdate: formatDate(ContactData?.signupdate),
        signuptime: formatTs(ContactData?.signuptime * 1000),

        // ---------- RELATED DATA ----------
        transactions: ContactData?.transactions ?? [],
        client_deals: ContactData?.client_deals ?? [],
        client_projects: ContactData?.client_projects ?? [],
        stats: ContactData?.stats ?? [],

        /// project details
      };

      setStatus(ContactData?.contactstatus);
      setcontact(sdata);
      setIsLoading(true);
    } catch (error) {
      console.warn("Error : ", error);
    }
  };

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose();
      if (e.key.toLowerCase() === "f" && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        const el = document.getElementById("quickview-search");
        el?.focus();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  console.log("contact in fields", contact);

  const fields = useMemo(
    () => [
      // ---------- SUMMARY TAB ----------
      { tab: "summary", label: "Contact ID", value: contact?.id, icon: IdCard },
      { tab: "summary", label: "Name", value: contact?.name, icon: User },
      {
        tab: "summary",
        label: "Email",
        value: contact?.email,
        icon: Mail,
        copyable: true,
        link: `mailto:${contact?.email}`,
      },
      {
        tab: "summary",
        label: "Phone",
        value: contact?.phone,
        icon: Phone,
        copyable: true,
        link: `tel:${contact?.phone}`,
      },
      {
        tab: "summary",
        label: "Source",
        value: contact?.source,
        icon: ExternalLink,
      },
      {
        tab: "summary",
        label: "Page Source",
        value: contact?.page_source,
        icon: Globe,
      },
      {
        tab: "summary",
        label: "Created At",
        value: contact?.created_at,
        icon: CalendarDays,
      },
      { tab: "summary", label: "Notify", value: contact?.notify, icon: Bell },
      {
        tab: "summary",
        label: "Team Lead ID",
        value: contact?.teamleadid,
        icon: Users,
      },
      {
        tab: "summary",
        label: "Assigned To",
        value: contact?.assigned?.name,
        icon: UserCircle2,
      },

      // ---------- CUSTOMER INFORMATION TAB ----------
      {
        tab: "customer_information",
        label: "Alternate Email",
        value: contact?.altemail,
        icon: Mail,
        copyable: true,
        link: contact?.altemail ? `mailto:${contact?.altemail}` : null,
      },
      {
        tab: "customer_information",
        label: "Alternate Phone",
        value: contact?.alternatephone,
        icon: Phone,
        copyable: true,
        link: contact?.alternatephone ? `tel:${contact?.alternatephone}` : null,
      },
      {
        tab: "customer_information",
        label: "User Type",
        value: contact?.usertype,
        icon: FileText,
      },
      {
        tab: "customer_information",
        label: "Country",
        value: contact?.country,
        icon: Globe2,
      },
      {
        tab: "customer_information",
        label: "Tagline",
        value: contact?.tagline,
        icon: Quote,
      },
      {
        tab: "customer_information",
        label: "Company",
        value: contact?.companyname,
        icon: Briefcase,
      },
      {
        tab: "customer_information",
        label: "Address",
        value: contact?.address,
        icon: MapPin,
      },
      {
        tab: "customer_information",
        label: "City",
        value: contact?.city,
        icon: Building,
      },
      {
        tab: "customer_information",
        label: "State",
        value: contact?.state,
        icon: Building2,
      },
      {
        tab: "customer_information",
        label: "Zip Code",
        value: contact?.zipcode,
        icon: Map,
      },
      {
        tab: "customer_information",
        label: "Signup Date",
        value: contact?.signupdate,
        icon: Calendar,
      },
      {
        tab: "customer_information",
        label: "Signup Time",
        value: contact?.signuptime,
        icon: Clock,
      },
      {
        tab: "customer_information",
        label: "Badge",
        value: contact?.badge,
        icon: Award,
      },
    ],
    [contact, status]
  );

  const filteredFields = fields.filter(
    (f) =>
      (!query ||
        `${f.label} ${f.value}`.toLowerCase().includes(query.toLowerCase())) &&
      (activeTab === "summary"
        ? ["summary", "work", "personal"].includes(f.tab)
        : f.tab === activeTab)
  );

  useEffect(() => {
    GetData(data?.userid);
  }, [data]);

  console.log(data);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[60] flex items-stretch justify-end bg-slate-900/40 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.aside
            ref={containerRef}
            initial={{ x: 40, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 40, opacity: 0 }}
            transition={{ type: "spring", stiffness: 380, damping: 32 }}
            className="relative mt-auto mr-[2vh] mb-auto ml-0 flex h-[96vh] w-[1020px] max-w-[98vw] flex-col overflow-hidden rounded-3xl border border-solid border-slate-200 bg-gradient-to-b from-white to-slate-50 shadow-xl"
            aria-modal
            role="dialog"
          >
            {isLoading ? (
              <>
                {/* Header */}
                <div className="border-b border-solid border-slate-200 bg-white/90 p-4 backdrop-blur supports-[backdrop-filter]:bg-white/70 w-full">
                  <div className="flex flex-wrap items-center justify-between gap-3 w-full">
                    <div className="flex min-w-0 items-center gap-3">
                      <div className="flex items-center justify-center size-12 bg-slate-200 rounded-full overflow-hidden">
                        <p className="capitalize font-semi-bold text-base text-black">
                          {contact?.name
                            ?.split(" ")
                            ?.map((n, i) => n.charAt(0))}
                        </p>
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <h2 className="truncate text-lg font-semibold tracking-tight text-slate-900">
                            {contact?.name}
                          </h2>
                          <Badge
                            tone={contact?.active == "1" ? "green" : "red"}
                          >
                            {contact?.active == "1" ? "Active" : "Inactive"}
                          </Badge>
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-500">
                          <span className="inline-flex items-center gap-1">
                            <MailCheck className="size-4" /> {contact?.email}
                          </span>

                          <span className="inline-flex items-center gap-1">
                            <PhoneCall className="size-4" /> {contact?.phone}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      <Link
                        to={`/contacts/contact/${data.userid}`}
                        className="inline-flex items-center gap-1.5 rounded-xl border border-solid border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                      >
                        <ExternalLink className="size-4" /> View Full Profile
                      </Link>
                      <button
                        className="inline-flex items-center gap-1.5 rounded-xl bg-[#0B54FF] px-3 py-2 text-sm font-medium text-white hover:bg-[#0849e0]"
                        onClick={onClose}
                        aria-label="Close"
                      >
                        <X className="size-4" /> Exit
                      </button>
                    </div>
                  </div>

                  {/* Quick actions */}
                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    <QuickAction
                      icon={Mail}
                      label="Email"
                      href={`mailto:${contact?.email}`}
                    />
                    <QuickAction
                      icon={Phone}
                      label="Call"
                      href={`tel:${contact?.phone}`}
                    />
                    <div className="ml-auto flex items-center gap-2">
                      <kbd className="rounded-md border border-solid bg-white px-1.5 py-1 text-[10px] text-slate-500">
                        ⌘/Ctrl
                      </kbd>
                      <span className="text-[10px] text-slate-500">
                        + F to search
                      </span>
                    </div>
                  </div>
                </div>

                {/* Body */}
                <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 overflow-hidden p-4 w-full">
                  {/* Left: main content */}
                  <div className="col-span-12 flex min-h-0 flex-col gap-4">
                    {/* Top stats */}
                    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                      {contact?.stats?.map((ele, idx) => {
                        return (
                          <Stat
                            title={ele?.key}
                            value={ele?.value}
                            icon={CalendarDays}
                          />
                        );
                      })}
                    </div>

                    {/* Tabs */}
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex flex-wrap items-center gap-1">
                        {tabs?.map((t) => (
                          <button
                            key={t.key}
                            onClick={() => setActiveTab(t.key)}
                            className={classNames(
                              "rounded-md px-[12px] py-[8px] text-sm font-medium border border-solid border-[#E1E4EA]",
                              activeTab === t.key
                                ? "bg-[#F5F5F5] text-[#0E121B]"
                                : "text-slate-600 hover:bg-slate-100"
                            )}
                          >
                            {t.label}
                          </button>
                        ))}
                      </div>
                      <div className="relative">
                        <Search className="pointer-events-none absolute left-3 top-2.5 size-4 text-slate-400" />
                        <input
                          id="quickview-search"
                          placeholder="Search fields…"
                          value={query}
                          onChange={(e) => setQuery(e.target.value)}
                          className="w-64 rounded-2xl border border-solid border-slate-200 bg-white py-2 pl-9 pr-3 text-sm outline-none placeholder:text-slate-400 focus:ring-2 focus:ring-slate-900/10"
                        />
                      </div>
                    </div>

                    <div className="min-h-0 overflow-auto rounded-2xl py-2">
                      {/* Render fields for current tab (or summary) */}
                      {activeTab === "summary" ? (
                        <div className="space-y-4">
                          {/* ---------- IMPORTANT FIELDS ---------- */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {fields
                              .filter((f) => f.tab === "summary")
                              .map((field, idx) => (
                                <Field key={idx} {...field} />
                              ))}
                          </div>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 gap-3">
                          {filteredFields?.map((f, idx) => (
                            <Field key={idx} {...f} />
                          ))}

                          {activeTab === "notes" && <Notes />}
                          {activeTab === "assets" && (
                            <Assets assets={contact?.assets} />
                          )}
                          {activeTab === "activity" && (
                            <Activity items={contact?.activity} />
                          )}
                          {activeTab === "attendance" && (
                            <div className="space-y-3"> ... </div>
                          )}
                          {activeTab === "transactions" && (
                            <div className="space-y-3">
                              {contact?.transactions?.map((tx) => (
                                <Field
                                  key={tx.transactionid}
                                  label={`Transaction (${tx.transactiontype})`}
                                  value={`$${tx.amount} - ${tx.transactionstatus}`}
                                  icon={CheckCircle}
                                />
                              ))}
                            </div>
                          )}
                          {activeTab === "client_deals" && (
                            <div className="space-y-3">
                              {contact?.client_deals?.map((deal) => (
                                <Field
                                  key={deal.dealid}
                                  label={`Deal (${deal.deal_package})`}
                                  value={deal.name}
                                  icon={Briefcase}
                                />
                              ))}
                            </div>
                          )}
                          {activeTab === "client_projects" && (
                            <div className="space-y-3">
                              {contact?.client_projects?.map((prj) => (
                                <Field
                                  key={prj.projectid}
                                  label={`Project (${prj.invoiceid})`}
                                  value={`${prj.status} - $${prj.projectcost}`}
                                  icon={Layers}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between gap-2 border-t border-solid border-slate-200 bg-white p-3 w-full">
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <IdCard className="size-4" /> ID {contact?.userid}
                    <span>•</span>
                    <CalendarDays className="size-4" /> Joined{" "}
                    {formatDate(contact?.doj)}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm hover:bg-slate-50"
                      onClick={() => {
                        setActiveTab("notes");
                        setTimeout(() => {
                          const el = document.getElementById("addNotesField");
                          el?.focus();
                        }, 250);
                      }}
                    >
                      <StickyNote className="mr-1 inline size-4" />
                      Add Note
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <InnerLoader wrapperClass={"w-full"} />
            )}
          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function QuickAction({ icon: Icon, label, href, onClick }) {
  const className =
    "inline-flex items-center gap-1.5 rounded-xl border border-solid border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50";
  if (href)
    return (
      <a className={className} href={href}>
        <Icon className="size-4" /> {label}
      </a>
    );
  return (
    <button className={className} onClick={onClick}>
      <Icon className="size-4" /> {label}
    </button>
  );
}

function Activity({ items, compact = false }) {
  return (
    <ul className="space-y-3">
      {items?.map((a, i) => (
        <li key={i} className="flex items-start gap-3">
          <div className="mt-1 h-5 w-5 rounded-full bg-slate-100 ring-1 ring-slate-200" />
          <div className="min-w-0 flex-1">
            <div className="text-[13px] text-slate-800 pb-[5px]">{a.text}</div>
            <div className="text-[11px] text-slate-400">{formatTs(a.ts)}</div>
          </div>
        </li>
      ))}
      {!compact && (
        <button className="mt-1 inline-flex items-center gap-1 text-xs text-slate-600 hover:underline">
          View full activity <ChevronRight className="size-4" />
        </button>
      )}
    </ul>
  );
}

function Assets({ assets }) {
  return (
    <div className="space-y-3">
      {assets?.map((a, i) => (
        <div
          key={i}
          className="flex items-center justify-between rounded-xl border border-solid border-slate-200 bg-white p-3"
        >
          <div className="flex min-w-0 items-center gap-3">
            <Monitor className="size-5" />
            <div className="min-w-0">
              <div className="truncate text-sm font-medium">{a.name}</div>
              <div className="truncate text-xs text-slate-500">{a.id}</div>
            </div>
          </div>
          <Badge tone={a.status === "Assigned" ? "blue" : "grey"}>
            {a.status}
          </Badge>
        </div>
      ))}
      <button className="inline-flex items-center gap-1 text-xs text-slate-600 hover:underline">
        Manage assets <ChevronRight className="size-4" />
      </button>
    </div>
  );
}

function Notes() {
  const [notes, setNotes] = useState([
    {
      id: 1,
      text: "Great initiative on latest branding sprint. Keep momentum for Q4.",
      ts: Date.now() - 86400000,
    },
  ]);
  const [text, setText] = useState("");
  return (
    <div className="space-y-3">
      <div className="rounded-2xl border border-solid border-slate-200 bg-white p-3">
        <textarea
          id="addNotesField"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Add a manager note (visible to HR/Managers)…"
          className="min-h-[90px] w-full resize-y rounded-xl border border-solid border-slate-200 bg-slate-50 p-3 text-sm outline-none focus:ring-2 focus:ring-slate-900/10"
        />
        <div className="mt-2 flex justify-end">
          <button
            onClick={() => {
              if (!text.trim()) return;
              setNotes((n) => [{ id: Date.now(), text, ts: Date.now() }, ...n]);
              setText("");
            }}
            className="rounded-xl bg-slate-900 px-3 py-1.5 text-xs text-white hover:bg-slate-800"
          >
            Save Note
          </button>
        </div>
      </div>
      <ul className="space-y-2">
        {notes?.map((n) => (
          <li
            key={n.id}
            className="rounded-xl border border-solid border-slate-200 bg-white p-3"
          >
            <div className="text-sm text-slate-800">{n.text}</div>
            <div className="text-[11px] text-slate-400">
              {new Date(n.ts).toLocaleString()}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function formatTs(ts) {
  try {
    const d =
      typeof ts === "string" ? new Date(ts.replace(" ", "T")) : new Date(ts);
    return d.toLocaleString();
  } catch {
    return String(ts);
  }
}
